import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { partnerRoleSchema, type PartnerRole } from '@eis/core';
import { manifestBlocks, type ManifestBlock } from './manifest-form';

/**
 * View models for the Produkte surface.
 *
 * The word "version" is allowed here and only here (SSOT I-6): this is the product-engineering
 * persona's screen. The Group Policy surface must contain none of it, which is why these types live
 * in the tarifs feature rather than in a shared model both could reach for.
 */

/** protojson writes enum NAMES, prefixed with the enum's own name; the UI wants the bare value. */
export function bareEnum(value: string | undefined, prefix: string): string {
  if (value === undefined || value === '') return '';
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

export const releaseTypeOf = (version: ConfigApi.TarifVersion): string =>
  bareEnum(version.releaseType, 'RELEASE_TYPE_');

export const statusOf = (version: ConfigApi.TarifVersion): string =>
  bareEnum(version.status, 'ASSET_STATUS_');

/** `tarifservice:fahrrad/2026/1.1` → `1.1` (a draft: its id). The list labels versions, not ERNs. */
export function versionNameOf(version: ConfigApi.TarifVersion): string {
  const ern = version.ern ?? '';
  return ern.slice(ern.lastIndexOf('/') + 1);
}

export interface ManifestRow {
  readonly block: ManifestBlock;
  readonly key: string;
  readonly ern: string;
  /** The service the ERN is qualified with — the whole reason the digest is unambiguous (S-5). */
  readonly service: string;
}

/**
 * The manifest, flattened into rows grouped by block.
 *
 * `service` is split out rather than left inside the string because it is what the reader is
 * scanning for: the promotion work-list is "group the refs by service", and a table that made
 * you re-read the prefix on every row would hide the one structure that matters.
 */
export function manifestRows(manifest: unknown): ManifestRow[] {
  if (typeof manifest !== 'object' || manifest === null) return [];
  const document = manifest as Record<string, unknown>;

  return manifestBlocks.flatMap((block) => {
    const value: unknown = document[block];
    if (typeof value !== 'object' || value === null) return [];
    return Object.entries(value as Record<string, unknown>)
      .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, ern]) => ({
        block,
        key,
        ern,
        service: ern.includes(':') ? ern.slice(0, ern.indexOf(':')) : '',
      }));
  });
}

/** True when this version is the one its generation currently points at. */
export const isActiveVersion = (
  version: ConfigApi.TarifVersion,
  generation: ConfigApi.Generation | undefined,
): boolean =>
  version.ern !== undefined && version.ern !== '' && version.ern === generation?.activeVersionErn;

/**
 * The version's partner roles (SDD-011), read for the artifact library. Only rows the schema
 * accepts are shown — a stored artifact is what the gate approved, and a row that does not parse
 * would be a manifest this build cannot read rather than one to render half of.
 */
export function partnerRoleRows(manifest: unknown): PartnerRole[] {
  if (typeof manifest !== 'object' || manifest === null) return [];
  const roles: unknown = (manifest as Record<string, unknown>).partnerRoles;
  if (!Array.isArray(roles)) return [];
  return roles.flatMap((role) => {
    const parsed = partnerRoleSchema.safeParse(role);
    return parsed.success ? [parsed.data] : [];
  });
}

/** `1..1`, `0..*` — how a role's bounds read in a table. */
export const cardinalityOf = (role: PartnerRole): string =>
  `${String(role.minCount)}..${role.maxCount === undefined ? '*' : String(role.maxCount)}`;
